import { useBooleanFlag, getBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectIsPublisher } from 'redux/auth';
import {
  EOrganization,
  ESnapshotExists,
  Customer,
  CustomerOrganization,
  MailDelivery,
  exists,
  EUser,
  ENoticeDraft
} from 'lib/types';
import { State as States, CourtHouse as CourtHousesList } from 'lib/enums';
import {
  ConfirmAffidavitRecipientsData,
  confirmAffidavitRecipients,
  selectCurrentStepId,
  selectCustomerOrganizationRef,
  selectFiledBy,
  selectFiler,
  selectIsEditing,
  selectNewspaper,
  selectNoticeType,
  selectPreviousNoticeType
} from 'redux/placement';
import { useNoticeDataAffidavitSettings } from 'lib/frontend/hooks/useNoticeDataAffidavitSettings';
import {
  getAffidavitSettingsForNoticeData,
  NoticeWithAffidavitData
} from 'lib/pricing/affidavits';
import { requiresWetSignature } from 'lib/pricing/affidavitSettings';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import { getFirebaseContext } from 'utils/firebase';
import { ConfigurationError } from 'lib/errors/ColumnErrors';
import { getNewspaperErrorInfoForNewspaperIdWithDefaults } from 'lib/errors/templateErrors';
import {
  getNoticeTypeDefaultAddresses,
  isPublisherFundedMail
} from 'lib/mail/noticeTypeDefaultMail';
import { MailingAddress } from 'lib/types/organization';
import { columnObjectsAreEqual } from 'lib/utils/stringify';
import { getNoticeTypeFromNoticeData } from 'lib/helpers';
import { isAffidavitDisabled } from 'lib/affidavits';
import { MailedNoticeDocumentType } from 'lib/types/mail';
import { CONFIRM_PROOF } from '../helpers/calculatePlacementSteps';
import { verifyAddressDeliverability } from '../helpers/verifyAddressDeliverability';
import {
  actions as affidavitRecipientActions,
  DEFAULT_MAILING,
  DEFAULT_COURTHOUSE,
  hasPopulatedAddress,
  selectIsFormComplete
} from './deliveryRecipients.slice';
import {
  emptyErrors,
  validateRecipientsWithAlignment,
  deriveAffidavitAddOnPrefill,
  affidavitAddressRequired,
  noticeTypeDefaultRecipientsInvalid,
  getRecipientValidation
} from './deliveryRecipients.helpers';
import {
  resolveEffectiveAffidavitType,
  isWetSignatureDisabledForPublisher,
  areMailedDocsDisabledForPublisher
} from '../DeliveryAddOns/pricingConfig';

const {
  setSendAffidavitByMail,
  setCourthouseRecipients,
  setSendAffidavitToCourthouse,
  setMailingRecipients,
  setSendAffidavitByEmail,
  setRecipientValidationErrors,
  validateCourthouseRecipients,
  setShowErrors,
  validateMailingRecipients,
  setInvoiceRecipientValidationErrors,
  setInvoiceMailingRecipients,
  validateInvoiceMailingRecipients,
  setNoticeTypeDefaultRecipients,
  setAffidavitType,
  setShippingSpeed,
  setDeliveryOption,
  setWantsAdditionalAffidavit,
  resetAffidavitAddOnSelections,
  reset
} = affidavitRecipientActions;

export function useDeliveryRecipientsState({
  stepId,
  publisherOrganization,
  filer,
  draft
}: {
  stepId: string;
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  filer: ESnapshotExists<EUser> | undefined;
  draft: ESnapshotExists<ENoticeDraft>;
}) {
  const dispatch = useAppDispatch();
  const disableAddressVerification = useBooleanFlag(
    LaunchDarklyFlags.DISABLE_ADDRESS_VERIFICATION,
    false
  );
  const enableDeliveryAddOns = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_AFFIDAVIT_INVOICE_ADDONS,
    false
  );
  const state = useAppSelector(s => s.deliveryRecipients);

  // The state lives in the global store (a singleton), so reset it when the
  // placement flow unmounts to mirror the fresh-per-mount behavior the local
  // useReducer used to provide.
  useEffect(() => {
    return () => {
      dispatch(reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    sendAffidavitByEmail,
    sendAffidavitByMail,
    mailingRecipients,
    recipientValidationErrors,
    sendAffidavitToCourthouse,
    courthouseRecipients,
    courthouseValidationErrors
  } = state;
  const isPublisher = useAppSelector(selectIsPublisher);
  const editing = useAppSelector(selectIsEditing);
  const activeStepId = useAppSelector(selectCurrentStepId);
  const noticeType = useAppSelector(selectNoticeType);
  const previousNoticeType = useAppSelector(selectPreviousNoticeType);
  const [customer, setCustomer] = useState<ESnapshotExists<Customer> | null>(
    null
  );
  const [customerOrganization, setCustomerOrganization] =
    useState<ESnapshotExists<CustomerOrganization> | null>(null);
  const [sendEmailCheckEdited, setSendEmailCheckEdited] = useState(false);

  const defaultCourthouse =
    exists(publisherOrganization) && publisherOrganization.data().courthouseId
      ? CourtHousesList.find(
          c => c.id === publisherOrganization.data().courthouseId
        )
      : undefined;

  const placementNoticeType = useAppSelector(
    state => state.placement.noticeType
  );
  const placementMail = useAppSelector(state => state.placement.mail);
  const placementCustomer = useAppSelector(state => state.placement.customer);
  const placementCustomerOrganization = useAppSelector(
    selectCustomerOrganizationRef
  );
  const placementNewspaper = useAppSelector(selectNewspaper);
  const placementFiler = useAppSelector(selectFiler);
  const placementFiledBy = useAppSelector(selectFiledBy);
  const placementRequireEmailAffidavit = useAppSelector(
    state => state.placement.requireEmailAffidavit
  );
  const publisherOrganizationState = publisherOrganization?.data().state;

  // TODO: These settings combinations are a little hard to follow — is there a way we could make them more clear?
  const mailAffidavitsOutsideColumn =
    !!publisherOrganization?.data().mailAffidavitsOutsideColumn;

  const customNoticeType = getNoticeTypeFromNoticeData(
    {
      noticeType,
      previousNoticeType
    },
    publisherOrganization,
    {
      skipDisplayType: true
    }
  );

  const affidavitSettingsNoticeData: NoticeWithAffidavitData | undefined =
    placementNewspaper
      ? {
          newspaper: placementNewspaper,
          filer: placementFiler ?? undefined,
          filedBy: placementFiledBy ?? undefined,
          noticeType,
          previousNoticeType
        }
      : undefined;

  const {
    settings: affidavitSettings,
    loading: affidavitSettingsLoading,
    error: affidavitSettingsError
  } = useNoticeDataAffidavitSettings(
    getFirebaseContext(),
    affidavitSettingsNoticeData
  );

  // Falls back to the publisher layer so the first render doesn't flash the
  // non-AA copy and strip the paper's affidavit options. Safe because it only
  // drives messaging; nothing that reaches an invoice reads it.
  const affidavitsManagedByColumn =
    (
      affidavitSettings ??
      publisherOrganization?.data().affidavitReconciliationSettings
    )?.affidavitsManagedByColumn ?? false;

  // Resolved settings only — no fallback. This decides whether a wet-sig mail
  // record is created and whether placement promises the first copy is covered,
  // and the cascade inherits layers (parent org, customer organization) that
  // cannot be composed from the snapshots on hand. Guessing here is what let
  // placement and the invoice disagree — see lib/pricing/affidavitSettings.
  const noticeWillRequireWetSignature = requiresWetSignature(affidavitSettings);
  const customerMustReceiveMailedAffidavit = noticeWillRequireWetSignature;

  // An explicit "Never require a wet signature" override (notarizationVendor
  // 'notarize') on any of the three override layers opts out of the
  // wet-signature option, leaving a digital (RON) affidavit only.
  const customerNeverWetSignature =
    customer?.data().affidavitReconciliationSettings?.notarizationVendor ===
    'notarize';
  const customNoticeTypeNeverWetSignature =
    customNoticeType?.affidavitReconciliationSettings?.notarizationVendor ===
    'notarize';
  const customerOrganizationNeverWetSignature =
    customerOrganization?.data().affidavitReconciliationSettings
      ?.notarizationVendor === 'notarize';

  const hideEmailAffidavitOption =
    customerMustReceiveMailedAffidavit ||
    !!publisherOrganization?.data().hideDigitalAffidavits;

  const doesNotRequireEmailAffidavit = !(
    isPublisher ||
    !hideEmailAffidavitOption ||
    !!customer?.data().allowAffidavitEmail
  );

  const affidavitDisabled = publisherOrganization
    ? isAffidavitDisabled(
        { noticeType, previousNoticeType },
        publisherOrganization
      )
    : false;

  // Whether the wet-signature option is offered on this AA RON paper. The
  // newspaper opt-out (Ogden) is keyed off the notice's publisher so it applies
  // to any placer; the override-layer opt-outs come from their own ARS.
  const wetSigOffered =
    affidavitsManagedByColumn &&
    !noticeWillRequireWetSignature &&
    !isWetSignatureDisabledForPublisher(publisherOrganization) &&
    !customerNeverWetSignature &&
    !customerOrganizationNeverWetSignature &&
    !customNoticeTypeNeverWetSignature &&
    !areMailedDocsDisabledForPublisher(publisherOrganization);

  // Derive the effective affidavit type synchronously from org config so that
  // validateDeliveryAddOnsForm never sees null, even when the user clicks Next
  // before AffidavitAddOnsSection's initializer effect has flushed.
  const effectiveAffidavitType = resolveEffectiveAffidavitType({
    publisherOrganization,
    wetSigRequired: noticeWillRequireWetSignature,
    affidavitsManagedByColumn,
    storedAffidavitType: state.affidavitType,
    wetSigOffered
  });

  // TODO: Move validation handling to TextField validation system
  useEffect(() => {
    dispatch(validateMailingRecipients());
  }, [mailingRecipients]);

  // TODO: Move validation handling to TextField validation system
  useEffect(() => {
    dispatch(validateCourthouseRecipients());
  }, [courthouseRecipients]);

  // TODO: Move validation handling to TextField validation system
  useEffect(() => {
    dispatch(validateInvoiceMailingRecipients());
  }, [state.invoiceMailingRecipients]);

  // TODO: Can this recipients calculation be moved to a Redux action?
  const loadAffidavitsRecipients = () => {
    let mailAddresses: MailDelivery[] = [];
    // Read fresh on every entry path: saved mail carries a stale copy of the configuration.
    const newspaperAddresses = loadNewspaperMail({
      publisherOrganization,
      placementNoticeType
    });

    if (activeStepId === CONFIRM_PROOF || editing) {
      if (placementMail) {
        mailAddresses = placementMail;
      }
      if (enableDeliveryAddOns) {
        mailAddresses = mailAddresses.filter(m => !isPublisherFundedMail(m));
      }
    } else {
      const savedAddresses = loadFromSaved({ filer, editing });
      const placementAddresses = loadPlacementMail({ placementMail });

      const customerAddress = isPublisher
        ? loadCustomerMail({ customer })
        : loadFilerMail({ filer });

      if (customerAddress && sendAffidavitByMail) {
        mailAddresses = [customerAddress];
      } else if (!enableDeliveryAddOns && newspaperAddresses.length) {
        mailAddresses = newspaperAddresses;
      } else if (placementAddresses.length) {
        mailAddresses = placementAddresses;
      } else {
        mailAddresses = savedAddresses;
      }
    }

    if (enableDeliveryAddOns) {
      dispatch(setNoticeTypeDefaultRecipients(newspaperAddresses));
    }

    // Separate invoice mail before any affidavit gating: invoice mailing is
    // independent of affidavits, so saved invoice records must load even when
    // no affidavit is required.
    if (
      enableDeliveryAddOns &&
      (activeStepId === CONFIRM_PROOF || editing) &&
      mailAddresses.length
    ) {
      const invoiceItems = mailAddresses.filter(
        m => m.documentType === MailedNoticeDocumentType.INVOICE
      );
      if (invoiceItems.length) {
        dispatch(setInvoiceMailingRecipients(invoiceItems));
      }
      // Only affidavit items remain for the mailing recipients below
      mailAddresses = mailAddresses.filter(
        m => m.documentType !== MailedNoticeDocumentType.INVOICE
      );
    }

    if (affidavitDisabled) {
      dispatch(setSendAffidavitByMail(false));
      dispatch(setMailingRecipients([]));
      dispatch(setNoticeTypeDefaultRecipients([]));
      return;
    }

    if (customerMustReceiveMailedAffidavit) {
      dispatch(setSendAffidavitByMail(true));
    }

    // Prefill when editing and wet-sig enabled: derive add-on selections from
    // document types and shipping tiers stored on each mail item.
    if (
      enableDeliveryAddOns &&
      (activeStepId === CONFIRM_PROOF || editing) &&
      mailAddresses.length
    ) {
      // Derive the add-on opt-in UI state from the saved affidavit mail records
      // (excluding courthouse copies, handled below). A single mandatory wet-sig
      // copy is not treated as an opt-in — see deriveAffidavitAddOnPrefill.
      const prefill = deriveAffidavitAddOnPrefill(
        mailAddresses.filter(m => !m.isCourthouse),
        noticeWillRequireWetSignature
      );
      if (prefill.affidavitType) {
        dispatch(setAffidavitType(prefill.affidavitType));
      }
      if (prefill.shippingSpeed) {
        dispatch(setShippingSpeed(prefill.shippingSpeed));
      }
      // Wisconsin never mailed, but Lee and Hoffman did until now, so saved
      // notices still carry a mail selection the customer can no longer make.
      // Restoring it would keep asking for an address and pricing the copy.
      dispatch(
        setDeliveryOption(
          areMailedDocsDisabledForPublisher(publisherOrganization)
            ? 'email'
            : prefill.deliveryOption
        )
      );
      dispatch(setWantsAdditionalAffidavit(prefill.wantsAdditionalAffidavit));
    }

    const courthouseAddresses = mailAddresses.filter(item => item.isCourthouse);
    if (courthouseAddresses.length) {
      dispatch(setCourthouseRecipients(courthouseAddresses));
      dispatch(setSendAffidavitToCourthouse(true));
    }

    mailAddresses = mailAddresses.filter(item => !item.isCourthouse);
    // Wet-sig affidavits are mailed manually (never Lob), so the provider follows
    // the affidavit type/documentType, not merely whether the notice requires wet
    // sig — a customer can pick wet-sig on a paper that doesn't require it.
    const deriveProvider = (
      mailDelivery: MailDelivery
    ): MailDelivery['provider'] => {
      if (enableDeliveryAddOns && mailDelivery.documentType) {
        return mailDelivery.documentType ===
          MailedNoticeDocumentType.WET_SIG_AFFIDAVIT
          ? 'manual'
          : 'lob';
      }
      return noticeWillRequireWetSignature ? 'manual' : 'lob';
    };
    const defaultProvider: MailDelivery['provider'] =
      (enableDeliveryAddOns && effectiveAffidavitType === 'wet_sig') ||
      noticeWillRequireWetSignature
        ? 'manual'
        : 'lob';
    if (mailAddresses.length) {
      dispatch(
        setMailingRecipients(
          mailAddresses.map(mailDelivery => ({
            ...mailDelivery,
            provider: deriveProvider(mailDelivery)
          }))
        )
      );
      dispatch(setSendAffidavitByMail(true));
    } else {
      dispatch(
        setMailingRecipients([
          { ...DEFAULT_MAILING, provider: defaultProvider }
        ])
      );
    }
  };

  useEffect(() => {
    if (sendAffidavitByMail && !affidavitSettingsLoading) {
      loadAffidavitsRecipients();
    }
  }, [sendAffidavitByMail, affidavitSettingsLoading]);

  // TODO: Can/should this state be part of PlaceNoticeState
  useEffect(() => {
    // We rely on the customer from placement to avoid creating a duplicate customer
    // when the publisherOrganization and filer are set
    if (!placementCustomer?.id) {
      setCustomer(null);
      return;
    }

    void (async () => {
      const customerSnap = await placementCustomer?.get();
      if (exists(customerSnap)) {
        setCustomer(customerSnap);
      } else {
        setCustomer(null);
      }
    })();
  }, [placementCustomer?.id]);

  useEffect(() => {
    if (!placementCustomerOrganization?.id) {
      setCustomerOrganization(null);
      return;
    }

    void (async () => {
      const customerOrganizationSnap =
        await placementCustomerOrganization?.get();
      setCustomerOrganization(
        exists(customerOrganizationSnap) ? customerOrganizationSnap : null
      );
    })();
  }, [placementCustomerOrganization?.id]);

  // TODO: Can this be moved to a reducer?
  useEffect(() => {
    if (editing && !sendEmailCheckEdited) {
      dispatch(setSendAffidavitByEmail(placementRequireEmailAffidavit));
      setSendEmailCheckEdited(true);
    }
  }, [placementRequireEmailAffidavit]);

  // TODO: Can this be moved to a reducer?
  useEffect(() => {
    if (sendAffidavitToCourthouse && defaultCourthouse) {
      dispatch(
        setCourthouseRecipients(
          courthouseRecipients.map(c =>
            c.name === ''
              ? {
                  ...c,
                  courtHouse: defaultCourthouse.id,
                  name: defaultCourthouse.name,
                  address: {
                    address_line1: defaultCourthouse.address,
                    address_line2: '',
                    address_city: defaultCourthouse.city,
                    address_state: publisherOrganizationState || '',
                    state: States.kansas.value,
                    address_zip: defaultCourthouse.address.slice(
                      defaultCourthouse.address.length - 5
                    )
                  },
                  isCourthouse: true,
                  copies: 1
                }
              : c
          )
        )
      );
    }
  }, [sendAffidavitToCourthouse]);

  // TODO: Can this be moved to a reducer?
  useEffect(() => {
    if (!sendAffidavitByMail) {
      dispatch(
        setRecipientValidationErrors(emptyErrors(mailingRecipients.length))
      );
    }
  }, [activeStepId]);

  // Unfortunately, it's important that this useEffect happens after
  // the one above it (setting courthouse defaults). So don't move this!
  useEffect(() => {
    // Defer seeding until the settings cascade has resolved
    if (affidavitSettingsLoading) {
      return;
    }
    dispatch(setSendAffidavitByMail(false));
    // Clear any add-on opt-in carried over from a previous publisher/notice-type
    // so a stale "yes" can't prefill an address and charge the new publisher;
    // loadAffidavitsRecipients re-derives it from saved data when editing.
    if (enableDeliveryAddOns) {
      dispatch(resetAffidavitAddOnSelections());
      // Seed the paper's default affidavit type so completeness uses the flag-on
      // path deterministically (affidavitType !== null) instead of the legacy
      // path, which would demand an address the opt-out UI hides. Don't rely on
      // AffidavitAddOnsSection's sync effect — it may not have flushed yet.
      dispatch(
        setAffidavitType(
          resolveEffectiveAffidavitType({
            publisherOrganization,
            wetSigRequired: noticeWillRequireWetSignature,
            affidavitsManagedByColumn,
            storedAffidavitType: null
          })
        )
      );
    }
    loadAffidavitsRecipients();
  }, [
    filer?.id,
    publisherOrganization?.id,
    placementNoticeType,
    affidavitSettingsLoading
  ]);

  // Flag-on path: when the user picks a delivery mode that requires a physical
  // address (mail delivery or wet-sig type) and the form is still blank,
  // pre-fill with the filer/customer default address.
  useEffect(() => {
    if (!enableDeliveryAddOns) return;
    if (editing) return;
    // effectiveAffidavitType is derived from the settings cascade, so wait for
    // it to resolve rather than prefill (and price) from a guess.
    if (affidavitSettingsLoading) return;
    // A disabled-affidavit notice type collects no affidavit address at all.
    if (affidavitDisabled) return;
    // Prefill the default address whenever an address is actually required. The
    // paper-default affidavitType is seeded on context change, so this can't fire
    // for a RON/digital opt-out (no stale wet-sig charge).
    const needsAddress = affidavitAddressRequired(
      effectiveAffidavitType,
      state.deliveryOption
    );
    if (!needsAddress) return;
    if (mailingRecipients.some(hasPopulatedAddress)) return;
    const defaultAddress = isPublisher
      ? loadCustomerMail({ customer })
      : loadFilerMail({ filer });
    if (defaultAddress) {
      const provider: MailDelivery['provider'] =
        effectiveAffidavitType === 'wet_sig' ? 'manual' : 'lob';
      dispatch(setMailingRecipients([{ ...defaultAddress, provider }]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.deliveryOption,
    effectiveAffidavitType,
    state.wantsAdditionalAffidavit,
    affidavitSettingsLoading
  ]);

  // Flag-on path: when invoice mail mode is activated and the seeded row has no
  // address yet, pre-fill with the filer/customer default address.
  useEffect(() => {
    if (!enableDeliveryAddOns) return;
    if (editing) return;
    if (state.invoiceMailingRecipients.length === 0) return;
    if (state.invoiceMailingRecipients.some(hasPopulatedAddress)) return;
    const defaultAddress = isPublisher
      ? loadCustomerMail({ customer })
      : loadFilerMail({ filer });
    if (defaultAddress) {
      dispatch(
        setInvoiceMailingRecipients(
          state.invoiceMailingRecipients.map(r => ({
            ...r,
            ...defaultAddress
          }))
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.invoiceMailingRecipients.length]);

  // --- Flag-on path (delivery add-ons enabled) ---
  /**
   * Re-resolve the cascade at submit time to be sure we have the latest
   * settings e.g. server-side changes after the render-time query resolved
   * Falls back to the render-time value if the query fails, matching what
   * the customer was actually shown.
   */
  async function resolveAffidavitsManagedByColumn(): Promise<boolean> {
    if (!affidavitSettingsNoticeData) {
      return affidavitsManagedByColumn;
    }

    try {
      const settings = await getAffidavitSettingsForNoticeData(
        getFirebaseContext(),
        affidavitSettingsNoticeData
      );
      return settings?.affidavitsManagedByColumn ?? affidavitsManagedByColumn;
    } catch (e) {
      logAndCaptureException(
        ColumnService.WEB_PLACEMENT,
        e,
        'Placement: Error resolving affidavit settings on submit',
        { draft: draft.id }
      );
      return affidavitsManagedByColumn;
    }
  }

  /**
   * Validates the invoice mailing recipients and dispatches error state on
   * failure.
   */
  async function validateInvoiceRecipients(): Promise<
    { success: false } | { success: true; populated: MailDelivery[] }
  > {
    const { invoiceMailingRecipients } = state;
    dispatch(
      setInvoiceRecipientValidationErrors(
        emptyErrors(invoiceMailingRecipients.length)
      )
    );

    if (invoiceMailingRecipients.length === 0) {
      return { success: true, populated: [] };
    }

    if (!invoiceMailingRecipients.some(hasPopulatedAddress)) {
      const errors = emptyErrors(invoiceMailingRecipients.length);
      errors[0] = 'Please enter a mailing address to continue.';
      dispatch(setInvoiceRecipientValidationErrors(errors));
      dispatch(setShowErrors(true));
      return { success: false };
    }

    const result = await validateRecipientsWithAlignment(
      invoiceMailingRecipients,
      { disableAddressVerification, draft }
    );
    if (result.hasErrors) {
      dispatch(setInvoiceRecipientValidationErrors(result.errors));
      dispatch(setShowErrors(true));
      return { success: false };
    }

    return {
      success: true,
      populated: invoiceMailingRecipients.filter(hasPopulatedAddress)
    };
  }

  async function validateDeliveryAddOnsForm() {
    const failureData = {
      mailAffidavitsOutsideColumn,
      requireEmailAffidavit: false
    };

    const submitAffidavitsManagedByColumn =
      await resolveAffidavitsManagedByColumn();
    const submitEffectiveAffidavitType = resolveEffectiveAffidavitType({
      publisherOrganization,
      wetSigRequired: noticeWillRequireWetSignature,
      affidavitsManagedByColumn: submitAffidavitsManagedByColumn,
      storedAffidavitType: state.affidavitType,
      wetSigOffered
    });

    dispatch(
      setRecipientValidationErrors(emptyErrors(mailingRecipients.length))
    );
    dispatch(
      setInvoiceRecipientValidationErrors(
        emptyErrors(state.invoiceMailingRecipients.length)
      )
    );

    if (noticeTypeDefaultRecipientsInvalid(state.noticeTypeDefaultRecipients)) {
      await reportMisconfiguredNoticeTypeDefaults(
        publisherOrganization,
        state.noticeTypeDefaultRecipients
      );
      dispatch(setShowErrors(true));
      return { success: false, data: failureData };
    }

    // Require (and submit) an address whenever one is needed — including the
    // mandatory first copy on a wet-sig-required paper when the customer opted
    // into "only one". Without this a mandatory wet-sig copy would submit
    // mail:null and lose its delivery address.
    const needsAddress = affidavitAddressRequired(
      submitEffectiveAffidavitType,
      state.deliveryOption
    );

    function dispatchAffidavitFailure(errors: string[]) {
      dispatch(setRecipientValidationErrors(errors));
      dispatch(setShowErrors(true));
    }

    // Affidavit recipients validation
    if (needsAddress) {
      if (!mailingRecipients.some(hasPopulatedAddress)) {
        const errors = emptyErrors(mailingRecipients.length);
        errors[0] = 'Please enter a mailing address to continue.';
        dispatchAffidavitFailure(errors);
        return { success: false, data: failureData };
      }

      const affidavitResult = await validateRecipientsWithAlignment(
        mailingRecipients,
        { disableAddressVerification, draft }
      );
      if (affidavitResult.hasErrors) {
        dispatchAffidavitFailure(affidavitResult.errors);
        return { success: false, data: failureData };
      }
    }

    // Invoice recipients validation
    const invoiceValidation = await validateInvoiceRecipients();
    if (!invoiceValidation.success) {
      return { success: false, data: failureData };
    }

    // Build submission payload from populated rows only
    const affidavitPopulated = needsAddress
      ? mailingRecipients.filter(hasPopulatedAddress)
      : [];
    // `isNoticeTypeDefault` is the server's field, so don't echo it back. This is
    // hygiene, not a control — see the TODO on isPublisherFundedMail.
    const allMail = [...affidavitPopulated, ...invoiceValidation.populated].map(
      ({ isNoticeTypeDefault, ...mailDelivery }) => mailDelivery
    );

    // Picking wet-signature on a RON paper toggles it on via a notice-level ARS
    // override; `null` clears it when they keep the RON/digital default.
    const requestsWetSigOverride =
      submitEffectiveAffidavitType === 'wet_sig' &&
      !noticeWillRequireWetSignature;
    const data: ConfirmAffidavitRecipientsData = {
      mailAffidavitsOutsideColumn,
      requireEmailAffidavit: submitEffectiveAffidavitType !== 'wet_sig',
      mail: allMail.length > 0 ? allMail : null,
      noticeLevelAffidavitReconciliationSettings: requestsWetSigOverride
        ? { notarizationRequired: true, notarizationVendor: 'manual' }
        : null
    };

    return await dispatch(confirmAffidavitRecipients(data));
  }

  const validateDeliveryForm = async () => {
    const enableDeliveryAddOns = getBooleanFlag(
      LaunchDarklyFlags.ENABLE_AFFIDAVIT_INVOICE_ADDONS,
      false
    );

    if (affidavitDisabled) {
      const data: ConfirmAffidavitRecipientsData = {
        mailAffidavitsOutsideColumn,
        requireEmailAffidavit: false,
        mail: null,
        noticeLevelAffidavitReconciliationSettings: null
      };

      const invoiceValidation = await validateInvoiceRecipients();
      if (!invoiceValidation.success) {
        return { success: false, data };
      }
      if (invoiceValidation.populated.length > 0) {
        data.mail = invoiceValidation.populated;
      }
      return await dispatch(confirmAffidavitRecipients(data));
    }

    if (enableDeliveryAddOns) {
      return validateDeliveryAddOnsForm();
    }

    // Legacy path (flag off)
    const data: ConfirmAffidavitRecipientsData = {
      mailAffidavitsOutsideColumn,
      requireEmailAffidavit: doesNotRequireEmailAffidavit
        ? false
        : sendAffidavitByEmail
    };

    if (sendAffidavitByMail && recipientValidationErrors.some(e => !!e)) {
      dispatch(setShowErrors(true));
      return { success: false, data };
    }

    if (
      sendAffidavitToCourthouse &&
      courthouseValidationErrors.some(e => !!e)
    ) {
      dispatch(setShowErrors(true));
      return { success: false, data };
    }

    const isMail = mailingRecipients.some(hasPopulatedAddress);
    const isCourtHouse = !columnObjectsAreEqual(courthouseRecipients, [
      DEFAULT_COURTHOUSE
    ]);

    const newMail: MailDelivery[] = [];
    if (isMail) {
      newMail.push(...mailingRecipients);
    }
    if (isCourtHouse) {
      newMail.push(...courthouseRecipients);
    }

    data.mail = newMail.length > 0 ? newMail : null;

    if (sendAffidavitByMail) {
      const addressVerifications = await Promise.all(
        mailingRecipients.map(mailingAddress => {
          if (disableAddressVerification) {
            return true;
          }
          return verifyAddressDeliverability({ mailingAddress, draft });
        })
      );
      const hasUndeliverableAddress = addressVerifications.some(
        deliverable => !deliverable
      );

      if (hasUndeliverableAddress) {
        const newError = emptyErrors(mailingRecipients.length);
        addressVerifications.forEach((deliverable, index) => {
          if (!deliverable) {
            newError[index] =
              'The address you’ve entered is invalid. Please input a new address to ensure the affidavit will be delivered.';
          }
        });

        dispatch(setRecipientValidationErrors([...newError]));
        dispatch(setShowErrors(true));
        return { success: false, data };
      }
    }

    return await dispatch(confirmAffidavitRecipients(data));
  };

  // Nothing to fill in when no affidavit is required, so the section can never
  // hold up the step. affidavitDisabled is resolved synchronously from the
  // newspaper/notice-type data, so it doesn't wait on the cascade; everything
  // else must, so the step can't be completed against unsettled settings.
  const complete =
    affidavitDisabled ||
    (!affidavitSettingsLoading &&
      !affidavitSettingsError &&
      selectIsFormComplete(state, activeStepId, stepId));

  return {
    state,
    dispatch,
    validateDeliveryForm,
    complete,
    doesNotRequireEmailAffidavit,
    hideEmailAffidavitOption,
    customerMustReceiveMailedAffidavit,
    noticeWillRequireWetSignature,
    affidavitsManagedByColumn,
    wetSigOffered,
    affidavitDisabled,
    affidavitSettingsLoading
  };
}

const loadFromSaved = ({
  filer,
  editing
}: {
  filer: ESnapshotExists<EUser> | undefined;
  editing: boolean;
}): MailingAddress[] => {
  if (!exists(filer)) return [];
  if (!editing) return [];

  const { savedInfo } = filer.data();
  const savedMailAddresses = savedInfo?.mails;
  if (savedMailAddresses?.length) {
    return savedMailAddresses.map(savedMailAddress => ({
      description: '',
      ...savedMailAddress
    }));
  }
  return [];
};

const loadPlacementMail = ({
  placementMail
}: {
  placementMail: MailDelivery[] | null;
}): MailingAddress[] => {
  if (!placementMail?.length) return [];
  const nonDefaultAddresses = placementMail.filter(
    item => !item.isNoticeTypeDefault
  );
  if (nonDefaultAddresses.length) {
    return nonDefaultAddresses;
  }
  return [];
};

const loadFilerMail = ({
  filer
}: {
  filer: ESnapshotExists<EUser> | undefined;
}): MailingAddress | null => {
  if (!filer) return null;

  const { firstName, lastName, address, addressLine2, zipCode, city, state } =
    filer.data();

  const name = firstName && lastName ? `${firstName} ${lastName}` : '';

  return {
    name,
    address: {
      address_line1: address || '',
      address_line2: addressLine2 || '',
      address_zip: zipCode || '',
      address_city: city || '',
      address_state: state || ''
    },
    description: '',
    isCourthouse: false,
    copies: 1
  };
};

const loadCustomerMail = ({
  customer
}: {
  customer: ESnapshotExists<Customer> | null;
}): MailingAddress | null => {
  if (!customer) return null;

  const { firstName, lastName, address, addressLine2, zipCode, city, state } =
    customer.data();

  const name = firstName && lastName ? `${firstName} ${lastName}` : '';

  return {
    name,
    address: {
      address_line1: address || '',
      address_line2: addressLine2 || '',
      address_zip: zipCode || '',
      address_city: city || '',
      address_state: state || ''
    },
    description: '',
    isCourthouse: false,
    copies: 1
  };
};

const reportMisconfiguredNoticeTypeDefaults = async (
  publisherOrganization: ESnapshotExists<EOrganization> | undefined,
  recipients: MailDelivery[]
) => {
  if (!exists(publisherOrganization)) {
    return;
  }
  const newspaperInfo = await getNewspaperErrorInfoForNewspaperIdWithDefaults(
    getFirebaseContext(),
    publisherOrganization.id
  );
  const problems = recipients
    .map(recipient => ({
      recipient,
      validationError: getRecipientValidation(recipient)
    }))
    .filter(({ validationError }) => validationError !== '');

  logAndCaptureException(
    ColumnService.CSMS,
    new ConfigurationError({
      message: `Notice type default mailing addresses are undeliverable: ${problems
        .map(
          ({ recipient, validationError }) =>
            `'${recipient.name || '(unnamed)'}' — ${validationError}`
        )
        .join('; ')}`,
      toFix:
        'Correct the address under the notice type’s default mailing addresses in publisher settings',
      newspaperInfo
    }),
    'Placement blocked by undeliverable notice type default mailing address'
  );
};

const loadNewspaperMail = ({
  publisherOrganization,
  placementNoticeType
}: {
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  placementNoticeType: number;
}): MailingAddress[] => {
  if (!exists(publisherOrganization)) {
    return [];
  }
  return getNoticeTypeDefaultAddresses(
    publisherOrganization.data(),
    placementNoticeType
  ).map(mailingAddress => ({
    ...mailingAddress,
    copies: mailingAddress.copies ?? 1,
    isNoticeTypeDefault: true
  }));
};
